import React from "react";
import { motion } from "framer-motion";
import { LucideIcon, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ErrorBoundary from "@/components/common/ErrorBoundary";

interface AdminPageScaffoldProps {
  // Header
  title: string;
  description?: string;
  icon?: LucideIcon;
  actions?: React.ReactNode;
  
  // Layout
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '7xl' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  background?: 'default' | 'muted' | 'transparent';
  
  // Data states
  loading?: boolean;
  error?: Error | null;
  isEmpty?: boolean;
  emptyState?: React.ReactNode;
  
  // Motion
  enableMotion?: boolean;
  animationDelay?: number;
  
  children: React.ReactNode;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md', 
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full'
};

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6', 
  lg: 'p-8'
};

const backgroundClasses = {
  default: 'bg-background',
  muted: 'bg-muted/50',
  transparent: 'bg-transparent'
};

export const AdminPageScaffold: React.FC<AdminPageScaffoldProps> = ({
  title,
  description,
  icon: Icon,
  actions,
  maxWidth = 'full', // changed from '7xl' to 'full'
  padding = 'md',
  background = 'default',
  loading = false,
  error = null,
  isEmpty = false,
  emptyState,
  enableMotion = true,
  animationDelay = 0,
  children
}) => {
  const containerClass = `
    min-h-screen w-full mx-auto 
    ${maxWidthClasses[maxWidth]} 
    ${paddingClasses[padding]}
    ${backgroundClasses[background]}
  `.trim();

  const PageContent = () => (
    <div className={containerClass}>
      {/* Header Section - Only render if title is provided */}
      {title && (
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              {Icon && (
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
                {description && (
                  <p className="text-muted-foreground mt-1">{description}</p>
                )}
              </div>
            </div>
            {actions && (
              <div className="flex items-center gap-2">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error.message || "An error occurred while loading the page."}
          </AlertDescription>
        </Alert>
      )}

      {/* Content */}
      {isEmpty && emptyState ? (
        <div className="flex items-center justify-center min-h-[400px]">
          {emptyState}
        </div>
      ) : (
        <div className="space-y-6">
          {children}
        </div>
      )}
    </div>
  );

  if (enableMotion) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        <ErrorBoundary componentName="AdminPageScaffold">
          <motion.div variants={itemVariants}>
            <PageContent />
          </motion.div>
        </ErrorBoundary>
      </motion.div>
    );
  }

  return (
    <ErrorBoundary componentName="AdminPageScaffold">
      <PageContent />
    </ErrorBoundary>
  );
};